import { useCallback, useEffect, useState, useMemo } from 'react';
import { getTreatmentPlanItemList } from 'api/home';
import { TreatmentPlanItemVo } from 'types/home';
import dayjs from 'utils/dayjs';

const TEXT_VISIT_SERVICE_TYPE_MAP: Record<string, string> = {
    MEDICATION_REFILL: 'medication refill',
    PRESCRIPTION_REFILL: 'prescription refill',
    MEDICATION_CHANGE: 'Medication change',
    FOLLOW_UP_CONSULTATION: 'Follow-up consultation',
    OTHER: 'Other care needs',
    INITIAL_VISIT: 'Initial visit',
};

export type TVisitOption = {
    label: string;
    value: string;
    itemType: 'APPOINTMENT' | 'TEXT_VISIT';
    appointmentId?: number;
    textVisitUuid?: string;
    providerTreatmentSubmitted?: boolean;
    startAt?: string;
    endAt?: string;
    createdAt?: string;
    duration?: number;
    specialty?: string;
    serviceType?: string;
    contactType?: string;
    assessmentName?: string;
    patientSubmitAt?: string;
    updatedAt?: string;
};

const useLinkableVisits = (clientId?: number, enabled = true): [TVisitOption[], boolean, () => Promise<void>] => {
    const [items, setItems] = useState<TreatmentPlanItemVo[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const [inProgressRes, overdueRes, upcomingRes] = await Promise.all([
                getTreatmentPlanItemList({
                    clientId,
                    status: 'IN_PROGRESS',
                    orderKey: 'START_AT',
                    orderType: 'ASC',
                    current: 1,
                    size: 500,
                }),
                getTreatmentPlanItemList({
                    clientId,
                    status: 'OVERDUE',
                    orderKey: 'START_AT',
                    orderType: 'ASC',
                    current: 1,
                    size: 500,
                }),
                getTreatmentPlanItemList({
                    clientId,
                    status: 'UPCOMING',
                    orderKey: 'START_AT',
                    orderType: 'ASC',
                    current: 1,
                    size: 500,
                }),
            ]);
            const inProgressItems = inProgressRes?.data?.data?.list || [];
            const overdueItems = overdueRes?.data?.data?.list || [];
            const upcomingItems = upcomingRes?.data?.data?.list || [];
            setItems([...inProgressItems, ...overdueItems, ...upcomingItems]);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        if (clientId && enabled) {
            fetchData();
        }
    }, [clientId, enabled, fetchData]);

    const visitOptions = useMemo(() => {
        const result = items
            .filter((item) => item.itemType !== 'STANDALONE')
            .filter((item) => {
                if (item.itemType === 'APPOINTMENT' && item.appointment) {
                    return !item.appointment.providerTreatmentSubmitted;
                }
                if (item.itemType === 'TEXT_VISIT' && item.textVisit) {
                    return !item.textVisit.providerTreatmentSubmitted;
                }
                return false;
            })
            .map((item) => {
                if (item.itemType === 'APPOINTMENT' && item.appointment) {
                    const appt = item.appointment;
                    return {
                        label: `${appt.serviceSpecialty}: ${appt.serviceType} | ${appt.contactType}`,
                        value: `APPOINTMENT_${appt.id}`,
                        itemType: 'APPOINTMENT' as const,
                        appointmentId: appt.id,
                        providerTreatmentSubmitted: appt.providerTreatmentSubmitted,
                        startAt: appt.startAt,
                        endAt: appt.endAt,
                        duration: appt.duration,
                        specialty: appt.serviceSpecialty,
                        serviceType: appt.serviceType,
                        contactType: appt.contactType,
                    };
                }
                if (item.itemType === 'TEXT_VISIT' && item.textVisit) {
                    const tv = item.textVisit;
                    const isRefill = tv.serviceType === 'MEDICATION_REFILL' || tv.serviceType === 'PRESCRIPTION_REFILL';
                    const serviceTypeTitle = TEXT_VISIT_SERVICE_TYPE_MAP[tv.serviceType] || tv.serviceType;
                    const baseName = isRefill
                        ? `${tv.specialty || tv.assessmentName || ''} ${serviceTypeTitle}: Text visit`
                        : `${serviceTypeTitle}: Text visit`;
                    return {
                        label: baseName.trim(),
                        value: `TEXT_VISIT_${tv.textVisitUuid}`,
                        itemType: 'TEXT_VISIT' as const,
                        textVisitUuid: tv.textVisitUuid,
                        providerTreatmentSubmitted: tv.providerTreatmentSubmitted,
                        createdAt: tv.createdAt,
                        patientSubmitAt: tv.patientSubmitAt,
                        updatedAt: tv.updatedAt,
                        specialty: tv.specialty,
                        assessmentName: tv.assessmentName,
                        serviceType: tv.serviceType,
                    };
                }
                return null;
            })
            .filter(Boolean) as TVisitOption[];
        result.sort((a, b) => {
            const dateA = a.startAt || a.createdAt || '';
            const dateB = b.startAt || b.createdAt || '';
            return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
        });
        return result;
    }, [items]);

    return [visitOptions, loading, fetchData];
};

export default useLinkableVisits;
